import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { breadcrumbSchema, toolSchema } from "@/lib/schema";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";
import { ClusterLinks } from "@/components/ClusterLinks";

/**
 * /tcg-tools — Palworld TCG utility hub.
 * - Pull Rate Calculator (binomial: P(≥1 hit) = 1 - (1-p)^packs)
 * - Expected Pulls Estimator (E[hits], expected packs to first hit, 90% confidence packs)
 * - Pack Opening Tracker (localStorage) with analytics charts (rarity distribution + cumulative pulls)
 */

export const Route = createFileRoute("/tcg-tools")({
  head: () => ({
    meta: [
      { title: "Palworld TCG Tools — Pull Rates & Pack Tracker" },
      {
        name: "description",
        content:
          "Calculate Palworld Official Card Game Dawn of Palpagos pull rates, booster pack simulator odds and deck-building pack analytics.",
      },
      { property: "og:title", content: "Palworld TCG Tools — Pull Odds & Pack Tracker" },
      { property: "og:description", content: "Binomial pull-rate math, expected-pulls estimator, and pack analytics for Palworld TCG." },
      { property: "og:url", content: "https://palstars.com/tcg-tools" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Palworld TCG Tools" },
      { name: "twitter:description", content: "Pull rate calculator, expected pulls, and analytics for Palworld TCG." },
      
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/tcg-tools" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["TCG Tools", "/tcg-tools"]]),
      toolSchema({
        name: "Palworld TCG Tools",
        description: "Free Palworld Official Card Game Dawn of Palpagos pull rates, booster pack simulator odds, card game rules and deck building tracker.",
        path: "/tcg-tools",
      }),
    ],
  }),
  component: TcgToolsPage,
});

// Approximate published pull rates per pack (Dawn of Palpagos style box math).
const RARITY_RATES: { key: string; label: string; rate: number; color: string }[] = [
  { key: "C", label: "Common (C)", rate: 0.6, color: "#94a3b8" },
  { key: "U", label: "Uncommon (U)", rate: 0.25, color: "#22d3ee" },
  { key: "R", label: "Rare (R)", rate: 0.1, color: "#3b82f6" },
  { key: "DR", label: "Double Rare (DR)", rate: 0.05, color: "#8b5cf6" },
  { key: "SR", label: "Super Rare (SR)", rate: 0.0333, color: "#f59e0b" },
  { key: "SP", label: "Super Parallel (SP)", rate: 0.0167, color: "#f97316" },
  { key: "SSP", label: "Super Special Parallel (SSP)", rate: 0.0083, color: "#ef4444" },
  { key: "UR", label: "Ultra Rare / TSR", rate: 0.005, color: "#ec4899" },
];

type TrackerEntry = { id: string; ts: number; rarity: string; set: string };

function TcgToolsPage() {
  // Calculator state
  const [packs, setPacks] = useState(24);
  const [rarityKey, setRarityKey] = useState("SR");
  const selected = RARITY_RATES.find((r) => r.key === rarityKey)!;
  const p = selected.rate;
  const pAtLeastOne = 1 - Math.pow(1 - p, packs);
  const expectedHits = packs * p;
  const expectedPacksToFirst = 1 / p;
  // Packs needed for 90% chance of at least one hit
  const packsFor90 = Math.ceil(Math.log(1 - 0.9) / Math.log(1 - p));
  const packsFor50 = Math.ceil(Math.log(1 - 0.5) / Math.log(1 - p));
  const packsFor99 = Math.ceil(Math.log(1 - 0.99) / Math.log(1 - p));

  // Tracker
  const [entries, setEntries] = useState<TrackerEntry[]>([]);
  const [set, setSet] = useState("BP01");
  // Separate rarity for the cumulative chart so users can chart any tier, not only SR
  const [trackedRarity, setTrackedRarity] = useState("SR");
  useEffect(() => {
    try {
      const raw = localStorage.getItem("palstars.tcgTracker");
      if (raw) setEntries(JSON.parse(raw));
    } catch {
      /* ignore */
    }
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem("palstars.tcgTracker", JSON.stringify(entries));
    } catch {
      /* ignore */
    }
  }, [entries]);

  function log(rk: string) {
    setEntries((e) => [{ id: crypto.randomUUID(), ts: Date.now(), rarity: rk, set }, ...e].slice(0, 500));
  }
  function undoOne(rk: string) {
    // Remove the most recent entry matching this rarity (entries are stored newest-first)
    setEntries((e) => {
      const idx = e.findIndex((x) => x.rarity === rk);
      if (idx === -1) return e;
      const next = e.slice();
      next.splice(idx, 1);
      return next;
    });
  }
  function reset() {
    if (confirm("Clear all tracked pulls?")) setEntries([]);
  }

  const totals = useMemo(() => {
    const t: Record<string, number> = {};
    for (const r of RARITY_RATES) t[r.key] = 0;
    for (const e of entries) t[e.rarity] = (t[e.rarity] ?? 0) + 1;
    return t;
  }, [entries]);
  const totalPulls = entries.length;

  const trackedSelected = RARITY_RATES.find((r) => r.key === trackedRarity) ?? selected;

  // Cumulative-over-time series for the currently-tracked rarity
  const timeline = useMemo(() => {
    const asc = [...entries].reverse();
    let hits = 0;
    return asc.map((e, i) => {
      if (e.rarity === trackedRarity) hits++;
      return { i: i + 1, hits, ts: e.ts };
    });
  }, [entries, trackedRarity]);

  const faq = [
    {
      "@type": "Question",
      name: "How do I estimate expected pulls in Palworld TCG?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Multiply the number of packs by the rarity's per-pack rate. For SR at ~3.33% over 24 packs, expected hits = 24 × 0.0333 ≈ 0.8. Use the 1 − (1 − p)^n formula for chance of at least one hit.",
      },
    },
    {
      "@type": "Question",
      name: "How many packs do I need to pull an SSP card?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "SSP pull rate is roughly 0.83%. You need about 83 packs for a 50% chance, 275 for 90%, and 553 for 99% chance of at least one SSP hit.",
      },
    },
    {
      "@type": "Question",
      name: "What does the pack tracker measure?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "It records each pull you log locally in your browser and computes rarity distribution plus cumulative hit-rate over time, so you can compare your luck against expected odds.",
      },
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      
      <main className="mx-auto max-w-6xl px-4 py-8 space-y-10">
        <header className="space-y-2">
          <p className="text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Home</Link> / TCG Tools
          </p>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">Palworld TCG Tools</h1>
          <p className="aeo-answer text-muted-foreground max-w-3xl">
            Estimate expected pulls, calculate pack-opening odds, and visualize your rarity distribution with live analytics.
            All math uses the standard binomial pull-rate formula <code>P(≥1 hit) = 1 − (1 − p)ⁿ</code>.
          </p>
        </header>

        {/* Calculator + Expected Pulls */}
        <section className="rounded-xl border border-border bg-card/50 backdrop-blur p-5 space-y-4">
          <h2 className="text-xl font-bold">Pull Rate Calculator &amp; Expected Pulls Estimator</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Target rarity</span>
              <select
                value={rarityKey}
                onChange={(e) => setRarityKey(e.target.value)}
                className="rounded-md border border-border bg-background px-3 py-2"
              >
                {RARITY_RATES.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label} — {(r.rate * 100).toFixed(2)}%
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Packs to open</span>
              <input
                type="number"
                min={1}
                max={5000}
                value={packs}
                onChange={(e) => setPacks(Math.max(1, Math.min(5000, Number(e.target.value) || 1)))}
                className="rounded-md border border-border bg-background px-3 py-2"
              />
            </label>
            <div className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Per-pack odds</span>
              <div className="rounded-md border border-border bg-background px-3 py-2 font-mono">
                {(p * 100).toFixed(3)}%
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Chance of ≥1 hit" value={`${(pAtLeastOne * 100).toFixed(2)}%`} />
            <Stat label="Expected hits" value={expectedHits.toFixed(2)} />
            <Stat label="Avg packs / hit" value={expectedPacksToFirst.toFixed(1)} />
            <Stat label="Packs for 90% chance" value={String(packsFor90)} />
          </div>

          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            <Milestone label="50% confidence" packs={packsFor50} />
            <Milestone label="90% confidence" packs={packsFor90} />
            <Milestone label="99% confidence" packs={packsFor99} />
          </div>
          <p className="text-xs text-muted-foreground aeo-answer">
            <strong>Expected pulls</strong> for {selected.label}: with a {(p * 100).toFixed(2)}% per-pack rate you need roughly{" "}
            <strong>{packsFor50} packs for 50%</strong>, <strong>{packsFor90} for 90%</strong>, and{" "}
            <strong>{packsFor99} for 99%</strong> chance of at least one hit.
          </p>
        </section>

        {/* Tracker */}
        <section className="rounded-xl border border-border bg-card/50 backdrop-blur p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-xl font-bold">Pack Opening Tracker</h2>
            <div className="flex items-center gap-2 text-sm">
              <label className="flex items-center gap-1">
                <span className="text-muted-foreground">Set</span>
                <select
                  value={set}
                  onChange={(e) => setSet(e.target.value)}
                  className="rounded-md border border-border bg-background px-2 py-1"
                >
                  <option value="BP01">BP01 — Dawn of Palpagos</option>
                  <option value="BP02">BP02 — Legends Awaken</option>
                  <option value="TD01">TD01</option>
                  <option value="TD02">TD02</option>
                </select>
              </label>
              <button
                onClick={reset}
                className="rounded-md border border-border px-2 py-1 hover:bg-muted"
              >
                Reset
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {RARITY_RATES.map((r) => {
              const count = totals[r.key] ?? 0;
              return (
                <div
                  key={r.key}
                  className="flex items-center justify-between gap-2 rounded-md border bg-background/40 px-2 py-1.5 text-sm"
                  style={{ borderColor: r.color }}
                  title={r.label}
                >
                  <button
                    type="button"
                    onClick={() => undoOne(r.key)}
                    disabled={count === 0}
                    aria-label={`Remove one ${r.label}`}
                    title={`Remove one ${r.label}`}
                    className="rounded border px-1.5 leading-none hover:bg-muted disabled:opacity-30"
                  >
                    −
                  </button>
                  <span
                    className="flex-1 truncate text-center font-mono text-xs"
                    title={r.label}
                    style={{ color: r.color }}
                  >
                    {r.key} · {count}
                  </span>
                  <button
                    type="button"
                    onClick={() => log(r.key)}
                    aria-label={`Add one ${r.label}`}
                    title={`Add one ${r.label}`}
                    className="rounded border px-1.5 leading-none hover:bg-muted"
                  >
                    +
                  </button>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-muted-foreground">
            Tracked pulls: <strong>{totalPulls}</strong> — stored locally in your browser. Hover any rarity for its full card-tier name; use − to correct a mis-tap without resetting the whole tracker.
          </p>

          {/* Analytics charts */}
          <div className="grid lg:grid-cols-2 gap-4">
            <ChartCard title="Rarity distribution (your pulls vs. expected)">
              <DistributionChart totals={totals} total={totalPulls} />
              <p className="mt-2 text-[11px] text-muted-foreground leading-relaxed">
                Solid bar = pulls you have logged. Faded bar = statistically expected pulls at published rates for the same number of packs. Hover any bar for the exact count and full rarity name. If your solid bar is shorter than the faded bar you are running below expected value on that rarity.
              </p>
            </ChartCard>
            <ChartCard title={`Cumulative ${trackedSelected.label} hits over time`}>
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                <span className="text-muted-foreground">Chart rarity:</span>
                <select
                  value={trackedRarity}
                  onChange={(e) => setTrackedRarity(e.target.value)}
                  className="rounded-md border border-border bg-background px-2 py-1"
                  aria-label="Rarity to chart cumulatively"
                >
                  {RARITY_RATES.map((r) => (
                    <option key={r.key} value={r.key} title={r.label}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <span
                  className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5"
                  style={{ borderColor: trackedSelected.color, color: trackedSelected.color }}
                >
                  <span className="inline-block h-2 w-2 rounded-full" style={{ background: trackedSelected.color }} />
                  line color
                </span>
              </div>
              <CumulativeChart data={timeline} rarity={trackedSelected} color={trackedSelected.color} />
              <p className="mt-2 text-[11px] text-muted-foreground leading-relaxed">
                Pick any rarity above (not just Super Rare) to chart it. The line tracks your running total of <strong style={{ color: trackedSelected.color }}>{trackedSelected.label}</strong> pulls in that color. X axis = packs you have logged, Y axis = total hits for that rarity. Hover any point for an "after pack N you had X hits" tooltip.
              </p>
            </ChartCard>
          </div>

          {/* Tracker explanation panel */}
          <details className="rounded-md border border-border bg-background/40 p-3 text-sm" open>
            <summary className="cursor-pointer font-semibold">How to read your tracker</summary>
            <ul className="mt-2 list-disc pl-5 space-y-1 text-muted-foreground">
              <li>Tap <strong>+</strong> next to a rarity every time you pull that card in a pack; tap <strong>−</strong> if you mis-tapped.</li>
              <li>The <strong>Rarity distribution</strong> chart compares your logged pulls (solid bar) against statistically expected pulls (faded bar) for the same total pack count.</li>
              <li>The <strong>Cumulative</strong> chart shows how many hits of the selected rarity you had after each pack. A shallow slope means you're behind published odds; a steep slope means you're running hot.</li>
              <li>Everything is stored locally in this browser. Sign in and use the account page to sync it across devices.</li>
            </ul>
          </details>

          {/* Pull history table + export */}
          <div className="rounded-md border border-border bg-background/40 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-border">
              <h3 className="font-semibold text-sm">Pull history ({entries.length})</h3>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => exportCsv(entries)}
                  disabled={entries.length === 0}
                  className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted disabled:opacity-40"
                >Export CSV</button>
                <button
                  type="button"
                  onClick={() => exportJson(entries)}
                  disabled={entries.length === 0}
                  className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted disabled:opacity-40"
                >Export JSON</button>
              </div>
            </div>
            {entries.length === 0 ? (
              <p className="p-3 text-xs text-muted-foreground">No pulls logged yet. Tap + on any rarity above to start tracking.</p>
            ) : (
              <div className="max-h-64 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 sticky top-0">
                    <tr className="text-left">
                      <th className="px-3 py-1.5">#</th>
                      <th className="px-3 py-1.5">When</th>
                      <th className="px-3 py-1.5">Set</th>
                      <th className="px-3 py-1.5">Rarity</th>
                      <th className="px-3 py-1.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => {
                      const r = RARITY_RATES.find((x) => x.key === e.rarity);
                      return (
                        <tr key={e.id} className="border-t border-border">
                          <td className="px-3 py-1 text-muted-foreground">{entries.length - i}</td>
                          <td className="px-3 py-1">{new Date(e.ts).toLocaleString()}</td>
                          <td className="px-3 py-1">{e.set}</td>
                          <td className="px-3 py-1 font-semibold" style={{ color: r?.color }} title={r?.label}>
                            {r?.label ?? e.rarity}
                          </td>
                          <td className="px-3 py-1 text-right">
                            <button
                              type="button"
                              onClick={() => setEntries((all) => all.filter((x) => x.id !== e.id))}
                              className="rounded border border-border px-1.5 py-0.5 hover:bg-muted"
                              aria-label="Delete this pull"
                            >Delete</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <section className="rounded-xl border border-border bg-card/50 p-5">
          <h2 className="text-xl font-bold mb-2">Related tools &amp; guides</h2>
          <ul className="grid sm:grid-cols-2 gap-2 text-sm">
            <li><Link to="/cards" className="text-primary hover:underline">Full Palworld TCG card database →</Link></li>
            <li><Link to="/deck-builder" className="text-primary hover:underline">Deck Builder →</Link></li>
            <li><Link to="/guides/$slug" params={{ slug: "dawn-of-palpagos-rarities" }} className="text-primary hover:underline">Dawn of Palpagos rarities guide →</Link></li>
            <li><Link to="/guides/$slug" params={{ slug: "palworld-trial-deck" }} className="text-primary hover:underline">Trial deck guide →</Link></li>
            <li><Link to="/guides/$slug" params={{ slug: "palworld-tcg-god-pack" }} className="text-primary hover:underline">God pack odds guide →</Link></li>
            <li><Link to="/guides/$slug" params={{ slug: "palworld-card-prices-booster-box-demand" }} className="text-primary hover:underline">Card prices &amp; booster box demand →</Link></li>
            <li><Link to="/guides" className="text-primary hover:underline">Browse all Palworld guides →</Link></li>
          </ul>
        </section>

        <InternalLinkBlock path="/tcg-tools" />
        <ClusterLinks hub="/tcg-tools" heading="Palworld card game rules guides" />


        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faq,
            }),
          }}
        />
      </main>
      
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-background/60 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}

function Milestone({ label, packs }: { label: string; packs: number }) {
  return (
    <div className="rounded-lg border border-border bg-background/60 px-3 py-2 flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono font-bold">{packs} packs</span>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-background/60 p-3">
      <div className="text-sm font-semibold mb-2">{title}</div>
      {children}
    </div>
  );
}

function DistributionChart({ totals, total }: { totals: Record<string, number>; total: number }) {
  const W = 480, H = 220, padT = 18, padB = 42, padL = 8, padR = 8;
  const chartH = H - padT - padB;
  const chartW = W - padL - padR;
  const slotW = chartW / RARITY_RATES.length;
  const barW = (slotW - 6) / 2;
  const max = Math.max(1, ...RARITY_RATES.map((r) => Math.max(totals[r.key] ?? 0, r.rate * total)));
  const yBase = padT + chartH;
  return (
    <div className="overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="w-full h-auto block" role="img" aria-label="Rarity distribution chart">
        <text x={padL} y={12} fontSize="10" fill="currentColor" opacity={0.7}>Solid = your pulls · Faded = expected</text>
        {RARITY_RATES.map((r, i) => {
          const actual = totals[r.key] ?? 0;
          const expected = r.rate * total;
          const slotX = padL + i * slotW + 3;
          const hA = (actual / max) * chartH;
          const hE = (expected / max) * chartH;
          return (
            <g key={r.key}>
              <rect x={slotX} y={yBase - hA} width={barW} height={hA} fill={r.color} opacity={0.95}>
                <title>{`${r.label}: ${actual} actual pulls`}</title>
              </rect>
              <rect x={slotX + barW + 2} y={yBase - hE} width={barW} height={hE} fill={r.color} opacity={0.3}>
                <title>{`${r.label}: ${expected.toFixed(2)} expected`}</title>
              </rect>
              <text x={slotX + barW + 1} y={yBase + 14} textAnchor="middle" fontSize="10" fill="currentColor">{r.key}</text>
              <text x={slotX + barW + 1} y={yBase + 28} textAnchor="middle" fontSize="9" fill="currentColor" opacity={0.6}>{actual}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function CumulativeChart({ data, rarity, color }: { data: { i: number; hits: number }[]; rarity: { key: string; label: string }; color: string }) {
  const W = 480, H = 220, padT = 16, padB = 30, padL = 32, padR = 12;
  const chartH = H - padT - padB;
  const chartW = W - padL - padR;
  if (data.length === 0) {
    return <div className="text-xs text-muted-foreground py-12 text-center">Log some pulls above to see your cumulative {rarity.label} curve.</div>;
  }
  const maxI = data[data.length - 1].i;
  const maxH = Math.max(1, data[data.length - 1].hits);
  const cx = (i: number) => padL + (i / maxI) * chartW;
  const cy = (h: number) => padT + chartH - (h / maxH) * chartH;
  const poly = data.map((d) => `${cx(d.i)},${cy(d.hits)}`).join(" ");
  return (
    <div className="overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="w-full h-auto block" role="img" aria-label={`Cumulative ${rarity.label} hits chart`}>
        {/* axes */}
        <line x1={padL} y1={padT + chartH} x2={padL + chartW} y2={padT + chartH} stroke="currentColor" opacity={0.25} />
        <line x1={padL} y1={padT} x2={padL} y2={padT + chartH} stroke="currentColor" opacity={0.25} />
        {/* y-axis labels */}
        <text x={4} y={padT + 4} fontSize="10" fill="currentColor" opacity={0.7}>{maxH}</text>
        <text x={4} y={padT + chartH} fontSize="10" fill="currentColor" opacity={0.7}>0</text>
        <text x={padL - 4} y={padT - 4} fontSize="9" fill="currentColor" opacity={0.6} textAnchor="end">hits</text>
        {/* x-axis labels */}
        <text x={padL} y={H - 10} fontSize="10" fill="currentColor" opacity={0.7}>pack 1</text>
        <text x={padL + chartW} y={H - 10} fontSize="10" fill="currentColor" opacity={0.7} textAnchor="end">pack {maxI}</text>
        {/* line */}
        <polyline points={poly} fill="none" stroke={color} strokeWidth="2.5" />
        {data.map((d, idx) => (
          <circle key={idx} cx={cx(d.i)} cy={cy(d.hits)} r={3.5} fill={color}>
            <title>{`After pack ${d.i}: ${d.hits} ${rarity.key} hit${d.hits === 1 ? "" : "s"} logged`}</title>
          </circle>
        ))}
      </svg>
    </div>
  );
}

function download(name: string, mime: string, body: string) {
  const blob = new Blob([body], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function exportCsv(entries: TrackerEntry[]) {
  const rows = [["timestamp", "iso", "set", "rarity"]];
  for (const e of entries) rows.push([String(e.ts), new Date(e.ts).toISOString(), e.set, e.rarity]);
  download(`palstars-tcg-tracker-${Date.now()}.csv`, "text/csv", rows.map((r) => r.join(",")).join("\n"));
}
function exportJson(entries: TrackerEntry[]) {
  download(`palstars-tcg-tracker-${Date.now()}.json`, "application/json", JSON.stringify(entries, null, 2));
}

